import React, { useEffect, useState } from "react";
import QRCode from "qrcode";

interface AnimatedQRCodeProps {
  value: string;
  size?: number;
  logoSrc?: string;
  logoWidth?: number;
  logoHeight?: number;
  excavate?: boolean;
  color?: string;
}

const logoDataUrlCache = new Map<string, string>();

async function fetchImageAsDataUrl(url: string): Promise<string> {
  if (logoDataUrlCache.has(url)) return logoDataUrlCache.get(url)!;

  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error(`Failed to fetch logo: ${res.status}`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      logoDataUrlCache.set(url, dataUrl);
      resolve(dataUrl);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const AnimatedQRCode: React.FC<AnimatedQRCodeProps> = ({
  value,
  size = 200,
  logoSrc,
  logoWidth = 36,
  logoHeight = 18,
  excavate = false,
  color = "#111",
}) => {
  const [qrMatrix, setQrMatrix] = useState<boolean[][]>([]);
  const [revealOrder, setRevealOrder] = useState<number[][]>([]);
  const [revealed, setRevealed] = useState<number>(0);
  const [inlineLogo, setInlineLogo] = useState<string | null>(null);

  useEffect(() => {
    const qr = QRCode.create(value, { errorCorrectionLevel: "H" });
    const modules = qr.modules.data;
    const count = qr.modules.size;
    const matrix: boolean[][] = [];
    for (let i = 0; i < count; i++) {
      matrix.push([]);
      for (let j = 0; j < count; j++) {
        matrix[i].push(!!modules[i * count + j]);
      }
    }
    setQrMatrix(matrix);

    const center = Math.floor(count / 2);
    const cellList: { x: number; y: number; r: number }[] = [];
    for (let y = 0; y < count; y++) {
      for (let x = 0; x < count; x++) {
        const dx = x - center;
        const dy = y - center;
        const r = Math.sqrt(dx * dx + dy * dy);
        cellList.push({ x, y, r });
      }
    }
    cellList.sort((a, b) => a.r - b.r);
    setRevealOrder(cellList.map((c) => [c.x, c.y]));
    setRevealed(0);
  }, [value]);

  useEffect(() => {
    if (revealOrder.length === 0) return;
    if (revealed < revealOrder.length) {
      const t = setTimeout(
        () => setRevealed((v) => Math.min(v + 5, revealOrder.length)),
        10
      );
      return () => clearTimeout(t);
    }
  }, [revealed, revealOrder.length]);

  useEffect(() => {
    let mounted = true;
    if (!logoSrc) {
      setInlineLogo(null);
      return;
    }
    fetchImageAsDataUrl(logoSrc)
      .then((dataUrl) => {
        if (mounted) setInlineLogo(dataUrl);
      })
      .catch((err) => {
        if (mounted) setInlineLogo(null);
      });
    return () => {
      mounted = false;
    };
  }, [logoSrc]);

  if (!qrMatrix.length) return null;
  const count = qrMatrix.length;
  const cellSize = size / count;

  const logoX = (size - logoWidth) / 2;
  const logoY = (size - logoHeight) / 2;
  const logoStartX = Math.floor((logoX / size) * count);
  const logoStartY = Math.floor((logoY / size) * count);
  const logoEndX = Math.ceil(((logoX + logoWidth) / size) * count);
  const logoEndY = Math.ceil(((logoY + logoHeight) / size) * count);

  const isInsideLogo = (x: number, y: number) =>
    excavate &&
    x >= logoStartX &&
    x < logoEndX &&
    y >= logoStartY &&
    y < logoEndY;

  return (
    <svg
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="QR code"
    >
      {revealOrder
        .slice(0, revealed)
        .map(([x, y]) =>
          qrMatrix[y][x] && !isInsideLogo(x, y) ? (
            <rect
              key={`${x}-${y}`}
              x={x * cellSize}
              y={y * cellSize}
              width={cellSize}
              height={cellSize}
              fill={color}
            />
          ) : null
        )}
      {inlineLogo ? (
        <>
          <rect
            x={logoX - 6}
            y={logoY - 6}
            width={logoWidth + 12}
            height={logoHeight + 12}
            fill="white"
          />
          <image
            href={inlineLogo}
            x={logoX}
            y={logoY}
            width={logoWidth}
            height={logoHeight}
            style={{ pointerEvents: "none" }}
            preserveAspectRatio="xMidYMid meet"
          />
        </>
      ) : logoSrc ? (
        <>
          <rect
            x={logoX - 6}
            y={logoY - 6}
            width={logoWidth + 12}
            height={logoHeight + 12}
            fill="white"
          />
          <image
            href={logoSrc}
            x={logoX}
            y={logoY}
            width={logoWidth}
            height={logoHeight}
            style={{ pointerEvents: "none" }}
            preserveAspectRatio="xMidYMid meet"
          />
        </>
      ) : null}
    </svg>
  );
};

export default AnimatedQRCode;
